import { useCallback, useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Eye, EyeOff, User, Building2, ArrowRight, Sparkles, Rocket } from "lucide-react";
import { z } from "zod";
import logo from "@/assets/logo-chillwerk.png";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EmailVerificationCodeInput } from "@/components/auth/EmailVerificationCodeInput";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { InAppBrowserBanner } from "@/components/auth/InAppBrowserBanner";
import { LunaAvatar } from "@/components/dashboard/LunaAvatar";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { Helmet } from "react-helmet-async";
import { normalizeEmail } from "@/lib/normalizeEmail";
import { StartChoiceDialog } from "@/components/StartChoiceDialog";
import { ensureSessionAfterVerification } from "@/lib/ensureSessionAfterVerification";

const emailSchema = z.string().email("Ongeldig e-mailadres");
const passwordSchema = z.string()
  .min(8, "Wachtwoord moet minimaal 8 karakters zijn")
  .regex(/[a-z]/, "Wachtwoord moet minimaal één kleine letter bevatten")
  .regex(/[A-Z]/, "Wachtwoord moet minimaal één hoofdletter bevatten")
  .regex(/[0-9]/, "Wachtwoord moet minimaal één cijfer bevatten")
  .regex(/[^a-zA-Z0-9]/, "Wachtwoord moet minimaal één speciaal teken bevatten");

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const isSuggestionReturn = !!returnUrl && decodeURIComponent(returnUrl).startsWith('/voorstel/');
  const { user, signIn, resendConfirmation, isPasswordRecovery } = useAuth();
  const { toast } = useToast();
  const { trackFunnelEvent } = useVisitorTracking();
  
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [verificationUserId, setVerificationUserId] = useState<string | null>(null);
  const [verificationRecoveryLoading, setVerificationRecoveryLoading] = useState(false);
  const hasNavigatedAfterVerification = useRef(false);
  const verificationCodeSentRef = useRef(false);
  const verificationRecoveryStartedRef = useRef(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginErrors, setLoginErrors] = useState<{ email?: string; password?: string }>({});
  
  // Reset password dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  
  // Signup choice dialog
  const [signupChoiceOpen, setSignupChoiceOpen] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const startEmailVerificationRecovery = useCallback(async (email: string) => {
    const cleanEmail = normalizeEmail(email);
    const emailResult = emailSchema.safeParse(cleanEmail);
    if (!emailResult.success) return false;

    setVerificationRecoveryLoading(true);
    const { error } = await supabase.functions.invoke('request-email-verification-code', {
      body: { email: cleanEmail },
    });
    setVerificationRecoveryLoading(false);

    if (error) {
      toast({
        title: "Code versturen mislukt",
        description: error.message || "Probeer het later opnieuw.",
        variant: "destructive",
      });
      return false;
    }

    setConfirmationEmail(cleanEmail);
    setVerificationUserId(null);
    setAwaitingConfirmation(true);
    verificationCodeSentRef.current = true;
    toast({
      title: "Nieuwe verificatiecode verzonden 📧",
      description: "Vul de 6-cijferige code uit je inbox hieronder in.",
    });
    return true;
  }, [toast]);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setLoginEmail(normalizeEmail(emailParam));
      setResetEmail(normalizeEmail(emailParam));
    }

    const verifyEmail = searchParams.get('verifyEmail');
    if (!verifyEmail || verificationRecoveryStartedRef.current) return;

    verificationRecoveryStartedRef.current = true;
    const cleanEmail = normalizeEmail(verifyEmail);
    setLoginEmail(cleanEmail);
    void startEmailVerificationRecovery(cleanEmail);
  }, [searchParams, startEmailVerificationRecovery]);

  // Redirect if already logged in - go to dashboard based on user type
  // BUT: do NOT redirect during password recovery — user must set new password first
  useEffect(() => {
    if (isPasswordRecovery) return;
    if (user && !awaitingConfirmation && !hasNavigatedAfterVerification.current) {
      const redirectToDashboard = async () => {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('user_type, location, postal_code')
            .eq('id', user.id)
            .single();
          
          const { data: contactData } = await supabase
            .from('profile_contact_details')
            .select('email')
            .eq('id', user.id)
            .single();
          
          const userEmail = contactData?.email || user.email;
          
          if (data?.user_type === 'candidate') {
            const { data: candidateProfile } = await supabase
              .from('candidate_profiles')
              .select('email_verified_via_code, travel_distance, availability_min_hours, availability_max_hours, onboarding_completed, job_type')
              .eq('user_id', user.id)
              .single();
            
            // Email verificatie is VERPLICHT voor alle kandidaten
            if (!candidateProfile?.email_verified_via_code) {
              // Client-side guard: prevent duplicate verification code sends
              if (!verificationCodeSentRef.current) {
                verificationCodeSentRef.current = true;
                
                const { data: existingCode } = await supabase
                  .from('email_verification_codes')
                  .select('id')
                  .eq('user_id', user.id)
                  .eq('used', false)
                  .gt('expires_at', new Date().toISOString())
                  .maybeSingle();
                
                // Stuur nieuwe code als er geen actieve code is
                if (!existingCode) {
                  await supabase.functions.invoke('send-email-verification-code', {
                    body: { userId: user.id, email: userEmail }
                  });
                }
              }
              
              setAwaitingConfirmation(true);
              setConfirmationEmail(userEmail || '');
              setVerificationUserId(user.id);
              return;
            }
            
            // Check if there's a returnUrl - this comes from email links
            if (returnUrl) {
              navigate(decodeURIComponent(returnUrl));
              return;
            }
            
            // Check fase 1 completion - alleen redirect naar fase-1 als NIET compleet
            const phase1Complete = 
              data?.location?.trim() &&
              data?.postal_code?.trim() &&
              candidateProfile?.travel_distance?.trim() &&
              (candidateProfile?.availability_min_hours > 0 || candidateProfile?.availability_max_hours > 0) &&
              candidateProfile?.onboarding_completed;
            
            if (!phase1Complete) {
              // If job_type not set yet, go to type selection first
              const hasJobType = candidateProfile?.job_type;
              navigate(hasJobType ? '/kandidaat/profiel/edit/fase-1' : '/kandidaat/profiel/edit/type');
            } else {
              // Altijd naar dashboard - fase 2 alleen via email link met returnUrl
              navigate('/dashboard/kandidaat');
            }
          } else if (data?.user_type === 'company') {
            // Honor returnUrl (e.g. offer email link) before going to dashboard
            if (returnUrl) {
              navigate(decodeURIComponent(returnUrl));
              return;
            }
            // Companies now auto-approved, just go to dashboard
            navigate('/dashboard/bedrijf');
          } else {
            // Unknown/missing user_type (e.g. admin or pending): still honor returnUrl
            if (returnUrl) {
              navigate(decodeURIComponent(returnUrl));
              return;
            }
          }
        } catch (error) {
          console.error('Error checking user type:', error);
          if (returnUrl) {
            navigate(decodeURIComponent(returnUrl));
            return;
          }
          navigate('/dashboard/kandidaat');
        }
      };
      
      redirectToDashboard();
    }
  }, [user, navigate, awaitingConfirmation, returnUrl, isPasswordRecovery]);

  const validateLoginForm = () => {
    const errors: { email?: string; password?: string } = {};
    
    const emailResult = emailSchema.safeParse(normalizeEmail(loginEmail));
    if (!emailResult.success) {
      errors.email = emailResult.error.errors[0].message;
    }
    
    const passwordResult = passwordSchema.safeParse(loginPassword);
    if (!passwordResult.success) {
      errors.password = passwordResult.error.errors[0].message;
    }
    
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }
    
    setLoading(true);
    const cleanLoginEmail = normalizeEmail(loginEmail);
    const { error } = await signIn(cleanLoginEmail, loginPassword);

    if (error?.needsConfirmation) {
      await startEmailVerificationRecovery(cleanLoginEmail);
      setLoading(false);
      return;
    }
    
    if (!error) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: adminRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        if (adminRole) {
          sessionStorage.setItem('pending_admin_verification', 'true');
          sessionStorage.setItem('admin_email', cleanLoginEmail);
          navigate('/admin/verify-login');
          setLoading(false);
          return;
        }
      }
      // Direct terug naar de pagina uit de e-maillink
      if (returnUrl) {
        const target = decodeURIComponent(returnUrl);
        if (target.startsWith('/') && !target.startsWith('//')) {
          hasNavigatedAfterVerification.current = true;
          setLoading(false);
          navigate(target, { replace: true });
          return;
        }
      }
    }
    
    setLoading(false);
  };

  const handleVerified = async () => {
    try {
      trackFunnelEvent('email_verified', { user_id: verificationUserId });
      
      const data = { session: await ensureSessionAfterVerification(confirmationEmail || loginEmail, loginPassword) };
      
      if (!data.session?.user) {
        toast({
          title: "Verificatie geslaagd! ✓",
          description: "Je kunt nu inloggen met je gegevens.",
        });
        setAwaitingConfirmation(false);
        setVerificationUserId(null);
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', data.session.user.id)
        .single();
      
      if (profile?.user_type === 'candidate') {
        const { data: candidateProfile } = await supabase
          .from('candidate_profiles')
          .select('travel_distance, availability_min_hours, availability_max_hours, onboarding_completed, job_type')
          .eq('user_id', data.session.user.id)
          .single();
        
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('location, postal_code')
          .eq('id', data.session.user.id)
          .single();
        
        // Phase 1 is compleet als onboarding_completed true is (al eerder afgerond)
        // OF als alle velden zijn ingevuld
        const phase1Complete = 
          candidateProfile?.onboarding_completed ||
          (userProfile?.location?.trim() &&
          userProfile?.postal_code?.trim() &&
          candidateProfile?.travel_distance?.trim() &&
          (candidateProfile?.availability_min_hours > 0 || candidateProfile?.availability_max_hours > 0));
        
        hasNavigatedAfterVerification.current = true;
        setAwaitingConfirmation(false);
        setVerificationUserId(null);
        
        // Check if there's a returnUrl from email link
        if (returnUrl) {
          toast({
            title: "Welkom terug! 👋",
            description: "Je bent ingelogd, vul je profiel verder aan.",
          });
          navigate(decodeURIComponent(returnUrl));
        } else if (!phase1Complete) {
          toast({
            title: "Welkom bij ChillWerk! 👋",
            description: "Vul in 1 minuut je basis in en je bent live!",
          });
          // If job_type not set yet, go to type selection first
          const hasJobType = candidateProfile?.job_type;
          navigate(hasJobType ? '/kandidaat/profiel/edit/fase-1' : '/kandidaat/profiel/edit/type');
        } else {
          // Altijd naar dashboard - fase 2 alleen via email link met returnUrl
          toast({
            title: "Account geverifieerd! 🎉",
            description: "Je wordt doorgestuurd naar je dashboard...",
          });
          navigate('/dashboard/kandidaat');
        }
      } else {
        hasNavigatedAfterVerification.current = true;
        setAwaitingConfirmation(false);
        setVerificationUserId(null);
        
        toast({
          title: "Account geverifieerd! 🚀",
          description: "Welkom bij ChillWerk!",
        });
        navigate('/dashboard/bedrijf');
      }
    } catch (error) {
      console.error('Error in handleVerified:', error);
      toast({
        title: "Er ging iets mis",
        description: "Probeer opnieuw in te loggen.",
        variant: "destructive",
      });
      setAwaitingConfirmation(false);
      setVerificationUserId(null);
    }
  };

  const handleResetPassword = async () => {
    const cleanResetEmail = normalizeEmail(resetEmail);
    const emailResult = emailSchema.safeParse(cleanResetEmail);
    if (!emailResult.success) {
      toast({
        title: "Ongeldig e-mailadres",
        description: emailResult.error.errors[0].message,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.functions.invoke('send-password-reset', {
      body: {
        email: cleanResetEmail,
        redirectTo: 'https://chillwerk.nl/reset-password',
      },
    });

    setLoading(false);

    if (error || (data && (data as any).error)) {
      const message = (data as any)?.error || error?.message || "Probeer het later opnieuw.";
      toast({
        title: "Fout",
        description: message,
        variant: "destructive"
      });
    } else {
      setResetSent(true);
      const domain = cleanResetEmail.split("@")[1] || "";
      const isOutlookFamily = [
        "hotmail.com", "hotmail.nl", "outlook.com", "outlook.nl",
        "live.nl", "live.com", "msn.com",
      ].includes(domain);
      if (isOutlookFamily) {
        toast({
          title: "E-mail verzonden 📧 — let op!",
          description:
            "Outlook/Hotmail blokkeert vaak de reset-link. Gebruik in plaats daarvan de 6-cijferige code uit de mail op chillwerk.nl/reset-password.",
          duration: 12000,
        });
      } else {
        toast({
          title: "E-mail verzonden! 📧",
          description: "Check je inbox (en spamfolder) voor je reset link op ChillWerk.nl",
        });
      }
    }

  };

  const handleSignupChoice = (type: "candidate" | "company") => {
    setSignupChoiceOpen(false);
    if (type === "candidate") {
      navigate("/signup/kandidaat");
    } else {
      navigate("/start/bedrijven");
    }
  };

  // Backwards compatibility for older recovery links that still land on /auth
  if (isPasswordRecovery) {
    return <ResetPasswordForm />;
  }

  // Show code verification screen
  if (awaitingConfirmation && (verificationUserId || confirmationEmail)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src={logo} 
              alt="ChillWerk Logo" 
              className="h-20 mx-auto mb-4"
            />
          </div>
          
          <Card className="border-2 shadow-xl">
            <CardContent className="pt-6">
              <EmailVerificationCodeInput
                email={confirmationEmail}
                userId={verificationUserId}
                onVerified={verificationUserId ? handleVerified : async () => {
                  setAwaitingConfirmation(false);
                  const email = confirmationEmail;
                  setConfirmationEmail("");
                  setVerificationUserId(null);
                  toast({
                    title: "Account geverifieerd! 🎉",
                    description: "Log nu in met je nieuwe wachtwoord.",
                  });
                  navigate(`/auth?email=${encodeURIComponent(email)}`, { replace: true });
                }}
                onBack={async () => {
                  if (verificationUserId) {
                    await supabase.auth.signOut();
                  }
                  setAwaitingConfirmation(false);
                  setVerificationUserId(null);
                  setConfirmationEmail("");
                }}
                backLabel="Terug naar login"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-light to-white flex items-center justify-center px-4 py-8">
      <Helmet>
        <title>Inloggen | ChillWerk.nl</title>
        <meta name="description" content="Log in op ChillWerk.nl en bekijk jouw matches, berichten en vacatures. Of maak gratis een account aan en zet jezelf op de radar." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://chillwerk.nl/auth" />
        <meta property="og:title" content="Inloggen | ChillWerk.nl" />
        <meta property="og:description" content="Log in op ChillWerk.nl of maak gratis een account aan." />
        <meta property="og:url" content="https://chillwerk.nl/auth" />
      </Helmet>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-6">
          <img src={logo} alt="Chillwerk.nl" className="h-20 w-20 object-contain" loading="eager" />
        </Link>

        {/* In-app browser warning */}
        <InAppBrowserBanner variant="prominent" />

        {isSuggestionReturn && (
          <div className="mb-4 rounded-2xl border border-cyan-dark/20 bg-gradient-to-br from-cyan-light via-white to-yellow/10 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <LunaAvatar size="sm" />
              <p className="text-sm font-medium text-foreground/80 leading-relaxed">
                <span className="font-bold">👋 Luna heeft een toffe vacature voor je klaargezet.</span>{" "}
                Log even in om 'm te bekijken.
              </p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-2">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-heading font-black">
              {isSuggestionReturn ? "Nog één stapje" : "Welkom terug"}
            </CardTitle>
            <CardDescription>
              {isSuggestionReturn ? "Log in en bekijk direct je voorstel" : "Log in om verder te gaan"}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="font-bold">E-mailadres</Label>
                <Input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="jouw@email.nl"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="h-12 rounded-xl border-2"
                  required
                />
                {loginErrors.email && (
                  <p className="text-sm text-destructive">{loginErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="font-bold">Wachtwoord</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showLoginPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="h-12 rounded-xl border-2 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showLoginPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {loginErrors.password && (
                  <p className="text-sm text-destructive">{loginErrors.password}</p>
                )}
              </div>

              <Button 
                type="submit" 
                variant="cta" 
                size="lg" 
                className="w-full h-12 text-base font-black rounded-xl"
                disabled={loading}
              >
                {loading ? "Bezig met inloggen..." : "Inloggen"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setResetDialogOpen(true)}
                  className="text-sm text-cyan-dark hover:underline font-medium max-md:min-h-11 max-md:px-3"
                >
                  Wachtwoord vergeten?
                </button>
              </div>
            </form>

            {/* Live indicator: werkgevers actief op zoek — als sectie in de kaart */}
            <div className="mt-6 pt-5 border-t border-border">
              <button
                type="button"
                onClick={() => setSignupChoiceOpen(true)}
                className="w-full flex items-center justify-center gap-2.5 text-sm text-foreground hover:opacity-80 transition-opacity"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className="font-semibold">Werkgevers zijn nu actief op zoek naar talent</span>
              </button>
            </div>

          </CardContent>
        </Card>



        {/* Registreren blok */}
        <div className="mt-3 bg-white/80 backdrop-blur-sm rounded-2xl p-4 border-2 border-cyan/30 shadow-md">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-cyan/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-cyan-dark" />
              </div>
              <div>
                <p className="font-bold text-foreground text-sm">Nog geen account?</p>
                <p className="text-xs text-muted-foreground">100% gratis</p>
              </div>
            </div>
            <Button
              type="button"
              variant="cta"
              size="sm"
              className="font-bold rounded-xl whitespace-nowrap text-sm max-md:h-11"
              onClick={() => setSignupChoiceOpen(true)}
            >
              Registreren
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wachtwoord resetten</DialogTitle>
            <DialogDescription>
              Vul je e-mailadres in en we sturen je een link om je wachtwoord te resetten.
            </DialogDescription>
          </DialogHeader>
          {resetSent ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <Mail className="w-12 h-12 text-cyan" />
              <div className="text-center space-y-2">
                <p className="font-semibold">E-mail verzonden! 📧</p>
                <p className="text-sm text-muted-foreground">
                  Check je inbox <strong>én je spamfolder</strong> voor de reset link.
                </p>
              </div>
              <Button onClick={() => { setResetDialogOpen(false); setResetSent(false); }}>
                Sluiten
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">E-mailadres</Label>
                <Input
                  id="reset-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="jouw@email.nl"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
              <Button onClick={handleResetPassword} className="w-full" disabled={loading}>
                {loading ? "Bezig..." : "Verstuur reset link"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {verificationRecoveryLoading && (
        <div className="fixed inset-x-0 bottom-4 z-50 mx-auto w-fit rounded-full border bg-background px-4 py-2 text-sm font-medium shadow-lg">
          Verificatiecode versturen...
        </div>
      )}

      {/* Signup Choice Dialog — gedeelde startdialoog (zelfde als homepage) */}
      <StartChoiceDialog open={signupChoiceOpen} onOpenChange={setSignupChoiceOpen} />
    </div>
  );
};

export default Auth;
